import React, { FC, useState } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link, useNavigate } from "react-router-dom";
import ErrorModal from "components/Modal/errorModal";
import SuccessModal from "components/Modal/successModal";
import UnaffiliatedModal from "components/Modal/unaffiliatedModal"; // Nuevo modal personalizado
import config from "config.json";

export interface PageSignUpProps {
  className?: string;
}

interface FormData {
  first_name: string;
  last_name: string;
  document: string;
  phone_number: string;
  mail: string;
  password: string;
  birth_date: string;
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const url = config.backend_url;
  // const url = "http://localhost:3001";

  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    document: "",
    phone_number: "",
    mail: "",
    password: "",
    birth_date:""
  });

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isUnaffiliatedModalOpen, setIsUnaffiliatedModalOpen] = useState(false); // Estado del nuevo modal
  const [validationError, setValidationError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isCodeInputVisible, setIsCodeInputVisible] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [tokenError, setTokenError] = useState(""); // Estado del error del token
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleActivatePhone = (userId: string) => {
     navigate(`/activate-phone?user=${userId}`);
  };

  // Función para validar el número de teléfono argentino
  const telArgentinoValido = (tel: string) => {
    const num = tel.replace(/\D+/g, "");
    const regex = /^(11|[2368]\d{2})\d{6,8}$/; // Nueva expresión regular
    return regex.test(num);
  };

  const validateForm = () => {
    const { first_name, last_name, document, phone_number, mail, password, birth_date } = formData;
    if (!first_name || !last_name || !document || !phone_number || !mail || !password || !birth_date) {
      setValidationError("Todos los campos son obligatorios");
      return false;
    }

    if (!telArgentinoValido(phone_number)) {
      setValidationError("El número de teléfono no tiene el formato correcto. Ejemplo: 1163091332");
      return false;
    }

    setValidationError("");
    return true;
  };

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const registerResponse = await fetch(`${url}/authe/register_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const registerData = await registerResponse.json();

      if (registerData.code === "200") {
        setUserId(registerData.message); // Asignar el ID generado
        handleActivatePhone(registerData.message)
      //   const phoneActivationResponse = await fetch(
      //     `${url}/authe/request_phone_activation?id=${registerData.message}`
      //   );

      //   const phoneActivationData = await phoneActivationResponse.json();

      //   if (phoneActivationData.code === "200") {
      //     //setIsCodeInputVisible(true); // Mostrar input para ingresar el código
      //     handleActivatePhone(registerData.message);
      //   } else if (phoneActivationData.code === "901") {
      //     setIsUnaffiliatedModalOpen(true); // Mostrar el modal de no afiliado
      //   } else {
      //     setErrorMessage("Error al solicitar activación de teléfono");
      //     setIsErrorModalOpen(true);
      //   }
      // } 
      // // Manejar el caso específico donde el usuario ya está registrado
      // else if (registerData.code === "1") {
      //   setErrorMessage("El usuario ya se encuentra registrado");
      //   setIsErrorModalOpen(true);
      // } 
      // Manejar otros códigos de error
      }else if(registerData.code === "901"){
        setIsUnaffiliatedModalOpen(true); // Mostrar el modal de no afiliado
      }else {
        setErrorMessage("Error en el registro");
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setErrorMessage("No se pudo completar el registro");
      setIsErrorModalOpen(true);
    }

    setIsLoading(false);
  };

  const validateToken = () => {
    if (verificationCode.length !== 6 || isNaN(Number(verificationCode))) {
      setTokenError("El código debe ser un número de 6 dígitos");
      return false;
    }
    setTokenError("");
    return true;
  };

  const handleConfirmPhone = async () => {
    if (!validateToken()) return; // Validar el token antes de enviarlo

    try {
      const confirmPhoneResponse = await fetch(
        `${url}/authe/confirm_phone?id=${userId}&token=${verificationCode}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      const confirmData = await confirmPhoneResponse.json();

      if (confirmData.code === "200") {
        setIsSuccessModalOpen(true); // Mostrar modal de éxito
      } else if (confirmData.code === "901") {
        setIsUnaffiliatedModalOpen(true); // Mostrar el modal de no afiliado
      } else {
        setErrorMessage("Error al confirmar el teléfono");
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setErrorMessage("No se pudo confirmar el teléfono");
      setIsErrorModalOpen(true);
    }
  };

  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        handleModalClose={() => (window.location.href = "/login")}
        title="Bienvenido"
        message="Ya poedes iniciar sesión en tu cuenta"
      />
      <ErrorModal
        isModalOpen={isErrorModalOpen}
        handleModalClose={() => setIsErrorModalOpen(false)}
        message={errorMessage}
      />
      <UnaffiliatedModal
        isModalOpen={isUnaffiliatedModalOpen} // Nuevo modal
        handleModalClose={() => {
          setIsUnaffiliatedModalOpen(false);
          window.location.href = "/";
        }}
      />
      <Helmet>
        <title>Registro - Turismo</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-10 mt-3 text-3xl md:text-5xl font-semibold text-neutral-900 dark:text-neutral-100 text-center">
          Registro
        </h2>
        <div className="max-w-md mx-auto space-y-6">
          {!isCodeInputVisible && (
            <form className="grid grid-cols-1 gap-6" onSubmit={handleSignUp}>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span>Nombre</span>
                  <Input type="text" name="first_name" onChange={handleInputChange} />
                </label>
                <label className="block">
                  <span>Apellido</span>
                  <Input type="text" name="last_name" onChange={handleInputChange} />
                </label>
              </div>
              <label className="block">
                <span>Fecha de nacimiento</span>
                <Input type="date" name="birth_date" onChange={handleInputChange} />
              </label>
              <label className="block">
                <span>Número de Documento</span>
                <Input type="number" name="document" onChange={handleInputChange} />
              </label>
              <label className="block">
                <span>Número celular {'(cod area) + Nro (sin 15)'}</span>
                <Input type="number" style={{fontSize: "12px"}} name="phone_number" placeholder="Ej: 1112345678" onChange={handleInputChange} />
                {validationError && validationError.includes("teléfono") && (
                  <p className="text-red-500">{validationError}</p>
                )}
              </label>
              <label className="block">
                <span>Email</span>
                <Input type="email" name="mail" onChange={handleInputChange} />
              </label>
              <label className="block">
                <span>Contraseña</span>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-4"
                  >
                    {showPassword ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
              </label>
              {validationError && !validationError.includes("teléfono") && (
                <p className="text-red-500 text-center">{validationError}</p>
              )}
              <p className="text-center text-neutral-500 dark:text-neutral-400 text-sm font-light">
                Al registrarte, enviaremos un código para confirmar tu cuenta.
              </p>
              <ButtonPrimary type="submit" disabled={isLoading}>
                Siguiente
              </ButtonPrimary>
            </form>
          )}

          {isCodeInputVisible && (
            <div className="flex flex-col items-center">
              <p className="text-center text-neutral-700 dark:text-neutral-300">
                Se envió un código de verificación a WhatsApp para verificar tu cuenta. Por favor, ingrésalo para completar el registro.
              </p>
              <label className="mt-2">Código de verificación</label>
              <Input
                type="number"
                required
                className="mt-2"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              {tokenError && <p className="text-red-500 my-2">{tokenError}</p>}
              <ButtonPrimary className="mt-2" onClick={handleConfirmPhone}>
                Completar registro
              </ButtonPrimary>
            </div>
          )}
          {!isCodeInputVisible && (
            <span className="flex justify-center text-center text-neutral-700 dark:text-neutral-300">
              ¿Ya tenes cuenta?{" "}
              <Link to="/login">
                <p className="text-primary-700 dark:text-primary-500"> &nbsp; Inicia Sesión</p>
              </Link>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;
